import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { getAdminNavOriginPathname } from "@/routing/adminNavModelQueries";
import type { SidebarNavigationItem } from "@/routing/adminRoutes";
import { buildAdminRevenueScopedPath } from "@/utils/adminRevenueAccessScope";
import {
  isBelowNavDivider,
  isSidebarItemActive,
  sidebarItemContainsActivePath,
} from "@/vnext/app-shell/navigationModel";
import { useShellDismiss } from "@/vnext/app-shell/useShellDismiss";
import { cx } from "@/vnext/shared/cx";

/** F-198: large counts cap at 99+, in the pill and in the row's name. */
const countText = (count: number) => (count > 99 ? "99+" : String(count));

/**
 * F-198: a pink count pill. The leading space keeps the pill's number a
 * separate word after the row label, so the label-in-name check (WCAG 2.5.3)
 * reads "Live alerts 3", not "Live alerts3"; flex layout does not render it.
 */
function CountBadge({ count }: { count: number }) {
  return (
    <>
      {" "}
      <span aria-hidden="true" className="vnext-shell-nav__badge">
        {countText(count)}
      </span>
    </>
  );
}

/** Page rows: "Live alerts, 3 for you"; sections say what is counted. */
const withCount = (label: string, count: number, what = "") =>
  count > 0 ? `${label}, ${countText(count)}${what} for you` : label;

const sectionCount = (item: SidebarNavigationItem) =>
  (item.children ?? []).reduce((sum, page) => sum + (page.badge ?? 0), 0);

function PageLink({
  item,
  onNavigate,
  pathname,
  search,
}: {
  item: SidebarNavigationItem;
  onNavigate?: () => void;
  pathname: string;
  search: string;
}) {
  if (!item.path) return null;
  const current = isSidebarItemActive(item, pathname);
  return (
    <Link
      aria-current={current ? "page" : undefined}
      aria-label={item.badge ? withCount(item.label, item.badge) : undefined}
      className={cx("vnext-shell-nav__page", current && "is-current")}
      onClick={onNavigate}
      to={buildAdminRevenueScopedPath(item.path, search)}
    >
      <span className="vnext-shell-nav__page-label">{item.label}</span>
      {item.badge ? <CountBadge count={item.badge} /> : null}
    </Link>
  );
}

/**
 * The sidebar sections and pages (F-009 to F-015, F-195 to F-198).
 * Expanded: only the current section's list is open; a closed section opens
 * at its first page, and clicking the open section closes its list without
 * leaving the page. Collapsed (F-200, F-201): icons only, and a section
 * opens a flyout of its pages. Every row comes from the navigation map's
 * access-checked sidebar items.
 */
export function ShellSidebarNav({
  collapsed,
  items,
  onNavigate,
}: {
  collapsed: boolean;
  items: SidebarNavigationItem[];
  onNavigate?: () => void;
}) {
  const { pathname: locationPathname, search } = useLocation();
  // A workbench opened from a dashboard marks that dashboard as current.
  const pathname =
    getAdminNavOriginPathname(locationPathname, search) ?? locationPathname;
  const navigate = useNavigate();
  const currentSectionId = useMemo(
    () =>
      items.find((item) => sidebarItemContainsActivePath(item, pathname))?.id ??
      null,
    [items, pathname],
  );
  const [closedOn, setClosedOn] = useState<string | null>(null);
  const openSectionId = closedOn === pathname ? null : currentSectionId;
  const [flyout, setFlyout] = useState<{
    id: string;
    top: number;
    left: number;
  } | null>(null);
  const flyoutRef = useRef<HTMLDivElement | null>(null);
  const railRef = useRef<HTMLDivElement | null>(null);
  const flyoutButtonRef = useRef<HTMLButtonElement | null>(null);
  const flyoutOpen = collapsed && flyout !== null;
  const closeFlyout = useCallback((restoreFocus: boolean) => {
    setFlyout(null);
    if (restoreFocus) flyoutButtonRef.current?.focus();
  }, []);
  useShellDismiss({
    open: flyoutOpen,
    onClose: closeFlyout,
    refs: useMemo(() => [flyoutRef, railRef], []),
  });
  // Cleared when the rail expands by any route (e.g. the shortcut), so it
  // cannot reappear at a stale spot when the rail collapses again.
  const [flyoutCollapsed, setFlyoutCollapsed] = useState(collapsed);
  if (flyoutCollapsed !== collapsed) {
    setFlyoutCollapsed(collapsed);
    if (!collapsed) setFlyout(null);
  }
  // Placed once in viewport coordinates, so it closes rather than drift when
  // the section list scrolls or the window resizes.
  useEffect(() => {
    if (!flyoutOpen) return undefined;
    const list = railRef.current?.closest(".vnext-sidebar__nav");
    const close = () => setFlyout(null);
    list?.addEventListener("scroll", close, { passive: true });
    window.addEventListener("resize", close);
    return () => {
      list?.removeEventListener("scroll", close);
      window.removeEventListener("resize", close);
    };
  }, [flyoutOpen]);
  const flyoutItem = flyout
    ? items.find((item) => item.id === flyout.id)
    : undefined;
  const firstBelowDivider = items.find((item) => isBelowNavDivider(item.id));

  const onSectionClick = (item: SidebarNavigationItem, target: HTMLElement) => {
    if (collapsed) {
      flyoutButtonRef.current =
        target instanceof HTMLButtonElement ? target : null;
      // Viewport coordinates: the section list scrolls (F-003), so an
      // absolutely placed flyout would be clipped by it (F-201).
      const rail = railRef.current?.getBoundingClientRect();
      setFlyout((current) =>
        current?.id === item.id
          ? null
          : {
              id: item.id,
              top: target.getBoundingClientRect().top,
              left: (rail?.right ?? target.getBoundingClientRect().right) + 10,
            },
      );
      return;
    }
    if (item.id === openSectionId) {
      setClosedOn(pathname);
      return;
    }
    const firstPage = item.children?.find((child) => child.path);
    setClosedOn(null);
    if (firstPage?.path) {
      onNavigate?.();
      void navigate(buildAdminRevenueScopedPath(firstPage.path, search));
    }
  };

  return (
    <div
      className={cx("vnext-shell-nav", collapsed && "is-collapsed")}
      ref={railRef}
    >
      {items.map((item) => {
        const divider =
          item === firstBelowDivider ? (
            <div aria-hidden="true" className="vnext-shell-nav__divider" />
          ) : null;
        const isCurrent = item.id === currentSectionId;
        if (!item.children?.length) {
          return (
            <Fragment key={item.id}>
              {divider}
              <Link
                aria-current={
                  item.path && isSidebarItemActive(item, pathname)
                    ? "page"
                    : undefined
                }
                aria-label={collapsed ? item.label : undefined}
                className={cx(
                  "vnext-shell-nav__section",
                  isCurrent && "is-current",
                )}
                data-tooltip={collapsed ? item.label : undefined}
                onClick={onNavigate}
                to={buildAdminRevenueScopedPath(item.path ?? "/", search)}
              >
                <span aria-hidden="true" className="vnext-shell-nav__icon">
                  {item.icon}
                </span>
                <span className="vnext-shell-nav__label">{item.label}</span>
              </Link>
            </Fragment>
          );
        }
        const isOpen = !collapsed && item.id === openSectionId;
        // A closed section (or the collapsed rail) repeats its pages' count.
        const closedCount = isOpen ? 0 : sectionCount(item);
        return (
          <Fragment key={item.id}>
            {divider}
            <button
              aria-current={isCurrent ? "location" : undefined}
              aria-expanded={collapsed ? flyout?.id === item.id : isOpen}
              aria-label={withCount(item.label, closedCount, " live alerts")}
              className={cx(
                "vnext-shell-nav__section",
                isCurrent && "is-current",
              )}
              data-tooltip={collapsed ? item.label : undefined}
              onClick={(event) => onSectionClick(item, event.currentTarget)}
              type="button"
            >
              <span aria-hidden="true" className="vnext-shell-nav__icon">
                {item.icon}
              </span>
              <span className="vnext-shell-nav__label">{item.label}</span>
              {closedCount > 0 ? <CountBadge count={closedCount} /> : null}
              <span aria-hidden="true" className="vnext-shell-nav__chevron">
                {isOpen ? (
                  <ExpandMoreOutlinedIcon sx={{ fontSize: 12 }} />
                ) : (
                  <ChevronRightOutlinedIcon sx={{ fontSize: 12 }} />
                )}
              </span>
            </button>
            {isOpen ? (
              <div className="vnext-shell-nav__pages">
                {item.children.map((child) => (
                  <PageLink
                    item={child}
                    key={child.id}
                    onNavigate={onNavigate}
                    pathname={pathname}
                    search={search}
                  />
                ))}
              </div>
            ) : null}
          </Fragment>
        );
      })}
      {flyoutOpen && flyoutItem?.children ? (
        <div
          aria-label={flyoutItem.label}
          className="vnext-shell-nav__flyout"
          ref={flyoutRef}
          role="group"
          style={{
            left: flyout.left,
            maxHeight: `calc(100vh - ${flyout.top}px - 8px)`,
            top: flyout.top,
          }}
        >
          <div className="vnext-shell-nav__flyout-title">
            {flyoutItem.label}
          </div>
          {flyoutItem.children.map((child) => (
            <PageLink
              item={child}
              key={child.id}
              onNavigate={() => {
                setFlyout(null);
                onNavigate?.();
              }}
              pathname={pathname}
              search={search}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
